import { cn } from '../../utils/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';

/** Shared by Button and IconButton so the two can never drift apart. */
export const VARIANT_CLASSES: Record<ButtonVariant, string> = {
    primary: 'bg-space-600 text-white hover:bg-space-500',
    secondary: 'bg-space-700 text-content-primary hover:bg-space-600',
    ghost: 'bg-transparent border border-space-700 text-content-muted hover:text-content-primary hover:border-hairline-strong',
    danger: 'bg-status-danger text-space-950 hover:brightness-110',
    accent: 'bg-[color:var(--zone-accent)] text-space-950 hover:brightness-110',
};

/**
 * Focus ring and disabled treatment. Deliberately no `pointer-events-none`:
 * see @docs/design-system.md on why a disabled control must not rely on a
 * `title` tooltip to explain itself.
 */
export const INTERACTIVE_CLASSES =
    'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hairline-strong focus-visible:ring-offset-2 focus-visible:ring-offset-space-900 disabled:opacity-40 disabled:cursor-not-allowed';

const LINK_BUTTON_SIZE_CLASSES: Record<'sm' | 'md', string> = {
    sm: 'h-7 px-2.5 text-label',
    md: 'h-9 px-3.5 text-value',
};

/**
 * Class string matching `<Button variant size>` for a router `<Link>` — the
 * `Button` primitive only renders a `<button>`, so a navigation link that
 * needs to look like one reuses these tables directly instead of every
 * page re-deriving its own copy (see ZonePage, ShipyardPage, SciencesPage).
 */
export function linkButtonClasses(variant: ButtonVariant, size: 'sm' | 'md' = 'sm'): string {
    return cn(
        'inline-flex items-center justify-center gap-1.5 rounded-control',
        LINK_BUTTON_SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        INTERACTIVE_CLASSES,
    );
}
